<template>
  <div class="field-value">
    <!-- Vazio -->
    <span v-if="isEmpty" class="text-muted">—</span>

    <!-- SQL -->
    <div v-else-if="isSqlField" class="sql-actions">
      <button class="btn btn-sm btn-info" @click="showSql = true">
        Ver SQL
      </button>
      <button
        v-if="hasCounterpart"
        class="btn btn-sm btn-secondary"
        @click="showDiff = true"
      >
        Ver Diff
      </button>
    </div>

    <!-- Imagem -->
    <div v-else-if="isImageField" class="img-wrapper">
      <img :src="value" :alt="field" class="img-thumb" @error="imgError = true" v-if="!imgError" />
      <a :href="value" target="_blank" rel="noopener" class="img-link">
        {{ truncateUrl(value) }}
      </a>
    </div>

    <!-- Cor -->
    <div v-else-if="isColorField">
      <span class="color-swatch" :style="{ backgroundColor: value }"></span>
      <span class="color-hex">{{ value }}</span>
    </div>

    <!-- Boolean -->
    <span
      v-else-if="isBoolean"
      class="badge"
      :class="value ? 'badge-success' : 'badge-secondary'"
    >
      {{ value ? 'Sim' : 'Não' }}
    </span>

    <!-- Array vazio -->
    <span v-else-if="Array.isArray(value) && value.length === 0" class="text-muted">
      — vazio —
    </span>

    <!-- Array de IDs com resolver (ex: produtos) -->
    <div v-else-if="isResolvableIdArray" class="ids-wrapper">
      <div class="ids-summary">
        <span class="badge badge-light ids-count">
          <fa-icon icon="cubes" /> {{ value.length }} {{ value.length === 1 ? 'item' : 'itens' }}
        </span>
        <span v-if="variant === 'depois' && diffCounts.added" class="badge badge-success">
          + {{ diffCounts.added }}
        </span>
        <span v-if="variant === 'depois' && diffCounts.removed" class="badge badge-danger">
          − {{ diffCounts.removed }}
        </span>
      </div>
      <!-- Detalhes só no depois (onde a comparação faz sentido) -->
      <template v-if="variant === 'depois' && (value.length > 0 || counterpartIds.length > 0)">
        <button
          class="btn btn-sm btn-outline-secondary btn-toggle-list"
          @click="toggleResolved"
        >
          <fa-icon :icon="expanded ? 'chevron-down' : 'chevron-right'" />
          {{ expanded ? 'Ocultar detalhes' : 'Ver detalhes' }}
        </button>
        <div v-if="expanded" class="resolved-area">
          <div v-if="resolving" class="text-muted small">
            <fa-icon icon="spinner" spin /> carregando nomes…
          </div>
          <div v-else-if="resolveError" class="text-danger small">
            Falha ao buscar nomes: {{ resolveError }}
          </div>
          <div v-else>
            <div v-if="diffLists.added.length" class="diff-block diff-added">
              <div class="diff-block-title">
                <fa-icon icon="plus-circle" /> Adicionados ({{ diffLists.added.length }})
              </div>
              <ul class="list-named list-scroll">
                <li v-for="item in diffLists.added" :key="'a-' + item.id">
                  <span class="item-id">#{{ item.id }}</span>
                  <span class="item-desc">{{ item.descricao || '—' }}</span>
                </li>
              </ul>
            </div>
            <div v-if="diffLists.removed.length" class="diff-block diff-removed">
              <div class="diff-block-title">
                <fa-icon icon="minus-circle" /> Removidos ({{ diffLists.removed.length }})
              </div>
              <ul class="list-named list-scroll">
                <li v-for="item in diffLists.removed" :key="'r-' + item.id">
                  <span class="item-id">#{{ item.id }}</span>
                  <span class="item-desc">{{ item.descricao || '—' }}</span>
                </li>
              </ul>
            </div>
            <div v-if="diffLists.kept.length" class="diff-block">
              <div class="diff-block-title text-muted">
                Mantidos ({{ diffLists.kept.length }})
              </div>
              <ul class="list-named list-scroll">
                <li v-for="item in diffLists.kept" :key="'k-' + item.id">
                  <span class="item-id">#{{ item.id }}</span>
                  <span class="item-desc">{{ item.descricao || '—' }}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </template>
    </div>

    <!-- Array de objetos {id, descricao} -->
    <div v-else-if="isArrayOfNamed" class="named-wrapper">
      <button
        v-if="isLargeNamedArray"
        class="btn btn-sm btn-outline-secondary btn-toggle-list"
        @click="expanded = !expanded"
      >
        <fa-icon :icon="expanded ? 'chevron-down' : 'chevron-right'" />
        {{ expanded ? 'Ocultar' : 'Ver' }} {{ value.length }} {{ value.length === 1 ? 'item' : 'itens' }}
      </button>
      <ul
        v-if="!isLargeNamedArray || expanded"
        class="list-named"
        :class="{ 'list-scroll': isLargeNamedArray }"
      >
        <li v-for="(item, idx) in value" :key="idx">
          <span class="item-id">#{{ item.id }}</span>
          <span class="item-desc">{{ item.descricao || '—' }}</span>
        </li>
      </ul>
    </div>

    <!-- Array genérico -->
    <div v-else-if="Array.isArray(value)" class="badge-list">
      <span v-for="(item, idx) in value" :key="idx" class="badge badge-light">
        {{ stringifyItem(item) }}
      </span>
    </div>

    <!-- Objeto -->
    <pre v-else-if="isObject" class="json-block">{{ prettyJson(value) }}</pre>

    <!-- Texto longo -->
    <span v-else-if="isLongText" class="long-text" :title="value">
      {{ value }}
    </span>

    <!-- Default: texto -->
    <span v-else>{{ value }}</span>

    <!-- Modal SQL -->
    <div v-if="showSql" class="modal fade show d-block sql-modal-overlay">
      <div class="modal-dialog modal-lg">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">SQL — {{ variant === 'antes' ? 'Antes' : 'Depois' }}</h5>
            <button type="button" class="close" @click="showSql = false">&times;</button>
          </div>
          <div class="modal-body">
            <CodeEditor
              :value="value || ''"
              :readonly="true"
              :scriptMode="'SQL'"
            />
          </div>
          <div class="modal-footer">
            <button class="btn btn-sm btn-primary" @click="copy">Copiar SQL</button>
            <button class="btn btn-sm btn-secondary" @click="showSql = false">Fechar</button>
          </div>
        </div>
      </div>
    </div>

    <!-- Modal Diff SQL -->
    <div v-if="showDiff" class="modal fade show d-block sql-modal-overlay">
      <div class="modal-dialog modal-xl" style="max-width: 95vw; width: 95vw;">
        <div class="modal-content" style="height: 90vh;">
          <div class="modal-header">
            <h5 class="modal-title">Diff SQL da Consulta</h5>
            <button type="button" class="close" @click="showDiff = false">&times;</button>
          </div>
          <div class="modal-body" style="overflow-y: auto; max-height: calc(90vh - 120px);">
            <CodeDiff
              :old-string="diffPayload.old"
              :new-string="diffPayload.new"
              output-format="side-by-side"
              :force-inline-comparison="true"
              language="sql"
            />
          </div>
          <div class="modal-footer">
            <button class="btn btn-sm btn-outline-secondary" @click="copyText(diffPayload.old)">
              Copiar SQL antigo
            </button>
            <button class="btn btn-sm btn-outline-primary" @click="copyText(diffPayload.new)">
              Copiar SQL novo
            </button>
            <button class="btn btn-sm btn-secondary" @click="showDiff = false">Fechar</button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CodeEditor from '@/components/form/CodeEditor.vue';
import { CodeDiff } from 'v-code-diff';

const IMG_PREFIXES = ['imagem', 'imagen', 'foto', 'avatar'];

export default {
  name: 'FieldValueRenderer',
  components: { CodeEditor, CodeDiff },
  props: {
    field: { type: String, required: true },
    value: {},
    variant: { type: String, default: 'antes' },
    counterpart: { default: null },
    resolver: { type: Function, default: null },
    resolvableFields: { type: Array, default: () => [] },
  },
  data() {
    return {
      showSql: false,
      showDiff: false,
      imgError: false,
      expanded: false,
      resolving: false,
      resolveError: null,
      resolvedMap: null,
    };
  },
  computed: {
    isEmpty() {
      return this.value === null || this.value === undefined || this.value === '';
    },
    isSqlField() {
      return String(this.field).toLowerCase() === 'sql' && typeof this.value === 'string';
    },
    isImageField() {
      const f = String(this.field).toLowerCase();
      return IMG_PREFIXES.some((p) => f.includes(p)) && typeof this.value === 'string';
    },
    isColorField() {
      const f = String(this.field).toLowerCase();
      return (f === 'cor' || f === 'color') && typeof this.value === 'string';
    },
    isBoolean() {
      return typeof this.value === 'boolean';
    },
    isArrayOfNamed() {
      return (
        Array.isArray(this.value) &&
        this.value.length > 0 &&
        typeof this.value[0] === 'object' &&
        this.value[0] !== null &&
        'id' in this.value[0] &&
        'descricao' in this.value[0]
      );
    },
    isLargeNamedArray() {
      return this.isArrayOfNamed && this.value.length > 10;
    },
    isResolvableIdArray() {
      if (!this.resolver || !Array.isArray(this.value) || this.value.length === 0) {
        return false;
      }
      if (!this.resolvableFields.includes(this.field)) {
        return false;
      }
      return this.value.every((v) => typeof v === 'number' || (typeof v === 'string' && /^\d+$/.test(v)));
    },
    counterpartIds() {
      if (!Array.isArray(this.counterpart)) return [];
      return this.counterpart.filter((v) => typeof v === 'number' || (typeof v === 'string' && /^\d+$/.test(v))).map(Number);
    },
    ownIds() {
      if (!Array.isArray(this.value)) return [];
      return this.value.map(Number);
    },
    diffCounts() {
      if (!this.isResolvableIdArray) return { added: 0, removed: 0 };
      const own = new Set(this.ownIds);
      const other = new Set(this.counterpartIds);
      if (this.variant === 'depois') {
        const added = this.ownIds.filter((id) => !other.has(id)).length;
        const removed = this.counterpartIds.filter((id) => !own.has(id)).length;
        return { added, removed };
      }
      return { added: 0, removed: 0 };
    },
    diffLists() {
      const own = new Set(this.ownIds);
      const other = new Set(this.counterpartIds);
      const mapName = (id) => ({
        id,
        descricao: this.resolvedMap ? this.resolvedMap[id] : null,
      });

      if (this.variant === 'depois') {
        return {
          added: this.ownIds.filter((id) => !other.has(id)).map(mapName),
          removed: this.counterpartIds.filter((id) => !own.has(id)).map(mapName),
          kept: this.ownIds.filter((id) => other.has(id)).map(mapName),
        };
      }
      return {
        added: [],
        removed: [],
        kept: this.ownIds.map(mapName),
      };
    },
    isObject() {
      return (
        typeof this.value === 'object' &&
        this.value !== null &&
        !Array.isArray(this.value)
      );
    },
    isLongText() {
      return typeof this.value === 'string' && this.value.length > 120;
    },
    hasCounterpart() {
      return typeof this.counterpart === 'string' && this.counterpart.length > 0;
    },
    diffPayload() {
      return this.variant === 'antes'
        ? { old: this.value || '', new: this.counterpart || '' }
        : { old: this.counterpart || '', new: this.value || '' };
    },
  },
  methods: {
    prettyJson(v) {
      try {
        return JSON.stringify(v, null, 2);
      } catch (_) {
        return String(v);
      }
    },
    stringifyItem(item) {
      if (item === null || item === undefined) return '—';
      if (typeof item === 'object') {
        try {
          return JSON.stringify(item);
        } catch (_) {
          return String(item);
        }
      }
      return String(item);
    },
    truncateUrl(url) {
      if (!url) return '';
      if (url.length <= 40) return url;
      return url.substring(0, 20) + '…' + url.substring(url.length - 15);
    },
    copy() {
      this.copyText(this.value);
    },
    // Copia o texto cru, preservando a formatação original do SQL (sem reindentar/normalizar).
    copyText(text) {
      if (typeof text === 'string' && text.length) {
        navigator.clipboard.writeText(text);
      }
    },
    async toggleResolved() {
      this.expanded = !this.expanded;
      if (!this.expanded || this.resolvedMap || !this.resolver) return;

      const allIds = Array.from(new Set([...this.ownIds, ...this.counterpartIds]));
      if (allIds.length === 0) {
        this.resolvedMap = {};
        return;
      }

      this.resolving = true;
      this.resolveError = null;
      try {
        const list = await this.resolver(this.field, allIds);
        const map = {};
        (list || []).forEach((item) => {
          if (item && item.id !== undefined) map[item.id] = item.descricao;
        });
        this.resolvedMap = map;
      } catch (err) {
        this.resolveError = err && err.message ? err.message : 'erro desconhecido';
      } finally {
        this.resolving = false;
      }
    },
  },
};
</script>

<style scoped>
.field-value {
  font-size: 13px;
}

.color-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 1px solid #ced4da;
  vertical-align: middle;
  margin-right: 6px;
}

.color-hex {
  font-family: monospace;
  font-size: 11px;
  vertical-align: middle;
}

.img-wrapper {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.img-thumb {
  max-width: 140px;
  max-height: 80px;
  object-fit: cover;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  background: #f8f9fa;
}

.img-link {
  font-size: 10px;
  word-break: break-all;
}

.named-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.btn-toggle-list {
  align-self: flex-start;
  font-size: 11px;
  padding: 3px 8px;
}

.btn-toggle-list svg {
  margin-right: 4px;
}

.list-named {
  list-style: none;
  padding: 0;
  margin: 0;
}

.list-scroll {
  max-height: 260px;
  overflow-y: auto;
  border: 1px solid #e9ecef;
  border-radius: 4px;
  padding: 4px 8px;
  background: #fafbfc;
}

.ids-wrapper {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.ids-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

.ids-count svg {
  margin-right: 3px;
}

.resolved-area {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.diff-block-title {
  font-size: 11px;
  font-weight: 600;
  margin-bottom: 3px;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}

.diff-added .diff-block-title {
  color: #146c43;
}

.diff-removed .diff-block-title {
  color: #b02a37;
}

.list-named li {
  padding: 2px 0;
  font-size: 12px;
  display: flex;
  gap: 6px;
  align-items: baseline;
}

.item-id {
  font-family: monospace;
  color: #667085;
  font-size: 10px;
  background: #f1f3f5;
  padding: 1px 5px;
  border-radius: 3px;
  flex-shrink: 0;
}

.item-desc {
  color: #1d2939;
}

.badge-list {
  display: flex;
  flex-wrap: wrap;
  gap: 3px;
}

.json-block {
  font-size: 10px;
  max-height: 160px;
  overflow: auto;
  margin: 0;
  padding: 4px 6px;
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 3px;
}

.long-text {
  display: inline-block;
  max-height: 60px;
  overflow: hidden;
  text-overflow: ellipsis;
}

.sql-actions {
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
}

.sql-modal-overlay {
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 10050;
}
</style>
